import { roundFloat, toDegrees } from '../util/math-util.js';
import { create, all } from 'mathjs';
var config = {};
var math = create(all, config);
function logHypot(a, b) {
    var _a = Math.abs(a);
    var _b = Math.abs(b);
    if (a === 0) {
        return Math.log(_b);
    }
    if (b === 0) {
        return Math.log(_a);
    }
    if (_a < 3000 && _b < 3000) {
        return Math.log(a * a + b * b) * 0.5;
    }
    return Math.log(a / Math.cos(Math.atan2(b, a)));
}
var P = {
    'w': 1,
    'x': 0,
    'y': 0,
    'z': 0,
};
function parse(dest, w, x, y, z) {
    if (z !== undefined) {
        dest['w'] = w;
        dest['x'] = x;
        dest['y'] = y;
        dest['z'] = z;
        return;
    }
    if (typeof w === 'object' && y === undefined) {
        if ('w' in w || 'x' in w || 'y' in w || 'z' in w) {
            dest['w'] = w['w'] || 0;
            dest['x'] = w['x'] || 0;
            dest['y'] = w['y'] || 0;
            dest['z'] = w['z'] || 0;
            return;
        }
        if ('re' in w && 'im' in w) {
            dest['w'] = w['re'];
            dest['x'] = w['im'];
            dest['y'] = 0;
            dest['z'] = 0;
            return;
        }
        if (w.length === 4) {
            dest['w'] = w[0];
            dest['x'] = w[1];
            dest['y'] = w[2];
            dest['z'] = w[3];
            return;
        }
        if (w.length === 3) {
            dest['w'] = 0;
            dest['x'] = w[0];
            dest['y'] = w[1];
            dest['z'] = w[2];
            return;
        }
        throw new Error('Invalid object');
    }
    if (typeof w === 'string' && y === undefined) {
        var tokens = w.match(/\d+\.?\d*e[+-]?\d+|\d+\.?\d*|\.\d+|./g);
        var plus = 1;
        var minus = 0;
        var iMap = { 'i': 'x', 'j': 'y', 'k': 'z' };
        if (tokens === null) {
            throw new Error('Parse error');
        }
        dest['w'] = 0;
        dest['x'] = 0;
        dest['y'] = 0;
        dest['z'] = 0;
        for (var i = 0; i < tokens.length; i++) {
            var c = tokens[i];
            var d = tokens[i + 1];
            if (c === ' ' || c === '\t' || c === '\n') {
            }
            else if (c === '+') {
                plus++;
            }
            else if (c === '-') {
                minus++;
            }
            else {
                if (plus + minus === 0) {
                    throw new Error('Parse error' + c);
                }
                var g = iMap[c];
                if (g !== undefined) {
                    if (d !== ' ' && !isNaN(d)) {
                        c = d;
                        i++;
                    }
                    else {
                        c = '1';
                    }
                }
                else {
                    if (isNaN(c)) {
                        throw new Error('Parser error');
                    }
                    g = iMap[d];
                    if (g !== undefined) {
                        i++;
                    }
                }
                dest[g || 'w'] += parseFloat((minus % 2 ? '-' : '') + c);
                plus = 0;
                minus = 0;
            }
        }
        if (plus + minus > 0) {
            throw new Error('Parser error');
        }
        return;
    }
    if (w === undefined && dest !== P) {
        dest['w'] = 1;
        dest['x'] = 0;
        dest['y'] = 0;
        dest['z'] = 0;
    }
    else {
        dest['w'] = w || 0;
        if (x && x.length === 3) {
            dest['x'] = x[0];
            dest['y'] = x[1];
            dest['z'] = x[2];
        }
        else {
            dest['x'] = x || 0;
            dest['y'] = y || 0;
            dest['z'] = z || 0;
        }
    }
}
function numToStr(n, char, prev) {
    var ret = '';
    if (n !== 0) {
        if (prev !== '') {
            ret += n < 0 ? ' - ' : ' + ';
        }
        else if (n < 0) {
            ret += '-';
        }
        n = Math.abs(n);
        if (1 !== n || char === '') {
            ret += n;
        }
        ret += char;
    }
    return ret;
}
export function Quaternion(w, x, y, z) {
    if (!(this instanceof Quaternion)) {
        return new Quaternion(w, x, y, z);
    }
    parse(this, w, x, y, z);
}
Quaternion.prototype = {
    'w': 1,
    'x': 0,
    'y': 0,
    'z': 0,
    'add': function (w, x, y, z) {
        parse(P, w, x, y, z);
        return new Quaternion(this['w'] + P['w'], this['x'] + P['x'], this['y'] + P['y'], this['z'] + P['z']);
    },
    'sub': function (w, x, y, z) {
        parse(P, w, x, y, z);
        return new Quaternion(this['w'] - P['w'], this['x'] - P['x'], this['y'] - P['y'], this['z'] - P['z']);
    },
    'neg': function () {
        return new Quaternion(-this['w'], -this['x'], -this['y'], -this['z']);
    },
    'norm': function () {
        var w = this['w'];
        var x = this['x'];
        var y = this['y'];
        var z = this['z'];
        return Math.sqrt(w * w + x * x + y * y + z * z);
    },
    'normSq': function () {
        var w = this['w'];
        var x = this['x'];
        var y = this['y'];
        var z = this['z'];
        return w * w + x * x + y * y + z * z;
    },
    'normalize': function () {
        var w = this['w'];
        var x = this['x'];
        var y = this['y'];
        var z = this['z'];
        var norm = Math.sqrt(w * w + x * x + y * y + z * z);
        if (norm < Quaternion['EPSILON']) {
            return Quaternion['ZERO'];
        }
        norm = 1 / norm;
        return new Quaternion(w * norm, x * norm, y * norm, z * norm);
    },
    'mul': function (w, x, y, z) {
        parse(P, w, x, y, z);
        var w1 = this['w'];
        var x1 = this['x'];
        var y1 = this['y'];
        var z1 = this['z'];
        var w2 = P['w'];
        var x2 = P['x'];
        var y2 = P['y'];
        var z2 = P['z'];
        return new Quaternion(w1 * w2 - x1 * x2 - y1 * y2 - z1 * z2, w1 * x2 + x1 * w2 + y1 * z2 - z1 * y2, w1 * y2 + y1 * w2 + z1 * x2 - x1 * z2, w1 * z2 + z1 * w2 + x1 * y2 - y1 * x2);
    },
    'scale': function (s) {
        return new Quaternion(this['w'] * s, this['x'] * s, this['y'] * s, this['z'] * s);
    },
    'dot': function (w, x, y, z) {
        parse(P, w, x, y, z);
        return this['w'] * P['w'] + this['x'] * P['x'] + this['y'] * P['y'] + this['z'] * P['z'];
    },
    'inverse': function () {
        var w = this['w'];
        var x = this['x'];
        var y = this['y'];
        var z = this['z'];
        var normSq = w * w + x * x + y * y + z * z;
        if (normSq === 0) {
            return Quaternion['ZERO'];
        }
        normSq = 1 / normSq;
        return new Quaternion(w * normSq, -x * normSq, -y * normSq, -z * normSq);
    },
    'div': function (w, x, y, z) {
        parse(P, w, x, y, z);
        var w1 = this['w'];
        var x1 = this['x'];
        var y1 = this['y'];
        var z1 = this['z'];
        var w2 = P['w'];
        var x2 = P['x'];
        var y2 = P['y'];
        var z2 = P['z'];
        var normSq = w2 * w2 + x2 * x2 + y2 * y2 + z2 * z2;
        if (normSq === 0) {
            return Quaternion['ZERO'];
        }
        normSq = 1 / normSq;
        return new Quaternion((w1 * w2 + x1 * x2 + y1 * y2 + z1 * z2) * normSq, (x1 * w2 - w1 * x2 - y1 * z2 + z1 * y2) * normSq, (y1 * w2 - w1 * y2 - z1 * x2 + x1 * z2) * normSq, (z1 * w2 - w1 * z2 - x1 * y2 + y1 * x2) * normSq);
    },
    'conjugate': function () {
        return new Quaternion(this['w'], -this['x'], -this['y'], -this['z']);
    },
    'exp': function () {
        var w = this['w'];
        var x = this['x'];
        var y = this['y'];
        var z = this['z'];
        var vNorm = Math.sqrt(x * x + y * y + z * z);
        var wExp = Math.exp(w);
        var scale = wExp / vNorm * Math.sin(vNorm);
        if (vNorm === 0) {
            return new Quaternion(wExp, 0, 0, 0);
        }
        return new Quaternion(wExp * Math.cos(vNorm), x * scale, y * scale, z * scale);
    },
    'log': function () {
        var w = this['w'];
        var x = this['x'];
        var y = this['y'];
        var z = this['z'];
        if (y === 0 && z === 0) {
            return new Quaternion(logHypot(w, x), Math.atan2(x, w), 0, 0);
        }
        var qNorm2 = x * x + y * y + z * z + w * w;
        var vNorm = Math.sqrt(x * x + y * y + z * z);
        var scale = Math.atan2(vNorm, w) / vNorm;
        return new Quaternion(Math.log(qNorm2) * 0.5, x * scale, y * scale, z * scale);
    },
    'pow': function (w, x, y, z) {
        parse(P, w, x, y, z);
        if (P['y'] === 0 && P['z'] === 0) {
            if (P['w'] === 1 && P['x'] === 0) {
                return this;
            }
            if (P['w'] === 0 && P['x'] === 0) {
                return Quaternion['ONE'];
            }
            if (this['y'] === 0 && this['z'] === 0) {
                var a = this['w'];
                var b = this['x'];
                if (a === 0 && b === 0) {
                    return Quaternion['ZERO'];
                }
                var arg = Math.atan2(b, a);
                var loh = logHypot(a, b);
                if (P['x'] === 0) {
                    if (b === 0 && a >= 0) {
                        return new Quaternion(Math.pow(a, P['w']), 0, 0, 0);
                    }
                    else if (a === 0) {
                        switch (P['w'] % 4) {
                            case 0:
                                return new Quaternion(Math.pow(b, P['w']), 0, 0, 0);
                            case 1:
                                return new Quaternion(0, Math.pow(b, P['w']), 0, 0);
                            case 2:
                                return new Quaternion(-Math.pow(b, P['w']), 0, 0, 0);
                            case 3:
                                return new Quaternion(0, -Math.pow(b, P['w']), 0, 0);
                            default:
                                break;
                        }
                    }
                }
                a = Math.exp(P['w'] * loh - P['x'] * arg);
                b = P['x'] * loh + P['w'] * arg;
                return new Quaternion(a * Math.cos(b), a * Math.sin(b), 0, 0);
            }
        }
        return this.log().mul(P)
            .exp();
    },
    'equals': function (w, x, y, z) {
        parse(P, w, x, y, z);
        var eps = Quaternion['EPSILON'];
        return Math.abs(P['w'] - this['w']) < eps
            && Math.abs(P['x'] - this['x']) < eps
            && Math.abs(P['y'] - this['y']) < eps
            && Math.abs(P['z'] - this['z']) < eps;
    },
    'isFinite': function () {
        return isFinite(this['w']) && isFinite(this['x']) && isFinite(this['y']) && isFinite(this['z']);
    },
    'isNaN': function () {
        return isNaN(this['w']) || isNaN(this['x']) || isNaN(this['y']) || isNaN(this['z']);
    },
    'toString': function () {
        var w = this['w'];
        var x = this['x'];
        var y = this['y'];
        var z = this['z'];
        var ret = '';
        if (isNaN(w) || isNaN(x) || isNaN(y) || isNaN(z)) {
            return 'NaN';
        }
        ret = numToStr(w, '', ret);
        ret += numToStr(x, 'i', ret);
        ret += numToStr(y, 'j', ret);
        ret += numToStr(z, 'k', ret);
        if ('' === ret) {
            return '0';
        }
        return ret;
    },
    'real': function () {
        return this['w'];
    },
    'imag': function () {
        return [this['x'], this['y'], this['z']];
    },
    'toVector': function () {
        return [this['w'], this['x'], this['y'], this['z']];
    },
    'toScalarLastVector': function () {
        return [this['x'], this['y'], this['z'], this['w']];
    },
    'toMatrix': function (d2) {
        var w = this['w'];
        var x = this['x'];
        var y = this['y'];
        var z = this['z'];
        var n = w * w + x * x + y * y + z * z;
        var s = n === 0 ? 0 : 2 / n;
        var wx = s * w * x, wy = s * w * y, wz = s * w * z;
        var xx = s * x * x, xy = s * x * y, xz = s * x * z;
        var yy = s * y * y, yz = s * y * z, zz = s * z * z;
        if (d2) {
            return [
                [1 - (yy + zz), xy - wz, xz + wy],
                [xy + wz, 1 - (xx + zz), yz - wx],
                [xz - wy, yz + wx, 1 - (xx + yy)],
            ];
        }
        return [
            1 - (yy + zz),
            xy - wz,
            xz + wy,
            xy + wz,
            1 - (xx + zz),
            yz - wx,
            xz - wy,
            yz + wx,
            1 - (xx + yy),
        ];
    },
    'toMatrix4': function (d2) {
        var w = this['w'];
        var x = this['x'];
        var y = this['y'];
        var z = this['z'];
        var n = w * w + x * x + y * y + z * z;
        var s = n === 0 ? 0 : 2 / n;
        var wx = s * w * x, wy = s * w * y, wz = s * w * z;
        var xx = s * x * x, xy = s * x * y, xz = s * x * z;
        var yy = s * y * y, yz = s * y * z, zz = s * z * z;
        if (d2) {
            return [
                [1 - (yy + zz), xy - wz, xz + wy, 0],
                [xy + wz, 1 - (xx + zz), yz - wx, 0],
                [xz - wy, yz + wx, 1 - (xx + yy), 0],
                [0, 0, 0, 1],
            ];
        }
        return [
            1 - (yy + zz),
            xy - wz,
            xz + wy,
            0,
            xy + wz,
            1 - (xx + zz),
            yz - wx,
            0,
            xz - wy,
            yz + wx,
            1 - (xx + yy),
            0,
            0,
            0,
            0,
            1,
        ];
    },
    'toBasisVectors': function () {
        var matrix = this.toMatrix(true);
        var basisVectors = [
            [matrix[0][0], matrix[1][0], matrix[2][0]],
            [matrix[0][1], matrix[1][1], matrix[2][1]],
            [matrix[0][2], matrix[1][2], matrix[2][2]],
        ];
        return basisVectors;
    },
    'clone': function () {
        return new Quaternion(this);
    },
    'rotateVector': function (v) {
        var w1 = this['w'];
        var x1 = this['x'];
        var y1 = this['y'];
        var z1 = this['z'];
        var x2 = v[0];
        var y2 = v[1];
        var z2 = v[2];
        var w3 = -x1 * x2 - y1 * y2 - z1 * z2;
        var x3 = w1 * x2 + y1 * z2 - z1 * y2;
        var y3 = w1 * y2 + z1 * x2 - x1 * z2;
        var z3 = w1 * z2 + x1 * y2 - y1 * x2;
        var x4 = x3 * w1 - w3 * x1 - y3 * z1 + z3 * y1;
        var y4 = y3 * w1 - w3 * y1 - z3 * x1 + x3 * z1;
        var z4 = z3 * w1 - w3 * z1 - x3 * y1 + y3 * x1;
        return [x4, y4, z4];
    },
    'slerp': function (w, x, y, z) {
        parse(P, w, x, y, z);
        var w1 = this['w'];
        var x1 = this['x'];
        var y1 = this['y'];
        var z1 = this['z'];
        var w2 = P['w'];
        var x2 = P['x'];
        var y2 = P['y'];
        var z2 = P['z'];
        var cosTheta0 = w1 * w2 + x1 * x2 + y1 * y2 + z1 * z2;
        if (cosTheta0 < 0) {
            w1 = -w1;
            x1 = -x1;
            y1 = -y1;
            z1 = -z1;
            cosTheta0 = -cosTheta0;
        }
        if (cosTheta0 > 0.9995) {
            return function (pct) {
                return new Quaternion(w1 + pct * (w2 - w1), x1 + pct * (x2 - x1), y1 + pct * (y2 - y1), z1 + pct * (z2 - z1))['normalize']();
            };
        }
        var Theta0 = Math.acos(cosTheta0);
        var sinTheta0 = Math.sin(Theta0);
        return function (pct) {
            var Theta = Theta0 * pct;
            var sinTheta = Math.sin(Theta);
            var cosTheta = Math.cos(Theta);
            var s0 = cosTheta - cosTheta0 * sinTheta / sinTheta0;
            var s1 = sinTheta / sinTheta0;
            return new Quaternion(s0 * w1 + s1 * w2, s0 * x1 + s1 * x2, s0 * y1 + s1 * y2, s0 * z1 + s1 * z2);
        };
    },
    'toEuler': function (seq, extrinsic) {
        if (extrinsic === void 0) { extrinsic = true; }
        if (seq.length !== 3) {
            throw Error('Expected 3 axes, got ' + seq);
        }
        var regex = new RegExp('^(?=[xyz]{3}$)');
        var validSeq = regex.test(seq);
        if (!validSeq) {
            throw Error('Expected seq to be from [\'x\', \'y\', \'z\'], got' + seq);
        }
        for (var i_1 = 0; i_1 < 2; ++i_1) {
            if (seq[i_1] === seq[i_1 + 1]) {
                throw Error('Expected consecutive axes to be different, got ' + seq);
            }
        }
        var angleFirst = extrinsic ? 0 : 2;
        var angleThird = extrinsic ? 2 : 0;
        if (!extrinsic) {
            seq = seq.split('')
                .reverse()
                .join('');
        }
        var elementaryBasisIndex = function (axis) {
            if (axis === 'x') {
                return 1;
            }
            if (axis === 'y') {
                return 2;
            }
            if (axis === 'z') {
                return 3;
            }
        };
        var i = elementaryBasisIndex(seq[0]);
        var j = elementaryBasisIndex(seq[1]);
        var k = elementaryBasisIndex(seq[2]);
        var symmetric = (i === k);
        if (symmetric) {
            k = 6 - i - j;
        }
        var sign = math.floor((i - j) * (j - k) * (k - i) / 2);
        var angles = [0, 0, 0];
        var eps = 1e-7;
        var q = this.toVector();
        var a = 0;
        var b = 0;
        var c = 0;
        var d = 0;
        if (symmetric) {
            a = q[0];
            b = q[i];
            c = q[j];
            d = q[k] * sign;
        }
        else {
            a = q[0] - q[j];
            b = q[i] + q[k] * sign;
            c = q[j] + q[0];
            d = q[k] * sign - q[i];
        }
        angles[1] = 2 * Math.atan2(Math.hypot(c, d), Math.hypot(a, b));
        var singularity = 0;
        if (Math.abs(angles[1]) <= eps) {
            singularity = 1;
        }
        else if (Math.abs(angles[1] - Math.PI) <= eps) {
            singularity = 2;
        }
        var halfSum = Math.atan2(b, a);
        var halfDiff = Math.atan2(d, c);
        if (singularity === 0) {
            angles[angleFirst] = halfSum - halfDiff;
            angles[angleThird] = halfSum + halfDiff;
        }
        else {
            angles[2] = 0;
            if (singularity === 1) {
                angles[0] = 2 * halfSum;
            }
            else {
                angles[0] = 2 * halfDiff * (extrinsic ? -1 : 1);
            }
        }
        if (!symmetric) {
            angles[angleThird] *= sign;
            angles[1] -= Math.PI / 2;
        }
        for (var index = 0; index < 3; ++index) {
            if (angles[index] < -Math.PI) {
                angles[index] += 2 * Math.PI;
            }
            else if (angles[index] > Math.PI) {
                angles[index] -= 2 * Math.PI;
            }
        }
        if (singularity !== 0) {
            console.warn('Gimbal lock detected. Setting third angle to zero since it ' +
                'is not possible to uniquely determine all angles.');
        }
        return angles.map(toDegrees);
    },
    'transform': function (targetFrameOrientation) {
        var q = new Quaternion(targetFrameOrientation);
        return q.mul(this).mul(q.conjugate());
    },
    'axisAngle': function () {
        if (Math.abs(this.w) === 1) {
            return { theta: 2 * Math.acos(this.w), axis: [1, 0, 0] };
        }
        return {
            theta: 2 * Math.acos(this.w),
            axis: [
                this.x / Math.sqrt(1 - this.w * this.w),
                this.y / Math.sqrt(1 - this.w * this.w),
                this.z / Math.sqrt(1 - this.w * this.w),
            ],
        };
    },
    'delta': function (q2) {
        return this.conjugate().mul(q2);
    },
    'round': function (precision) {
        return (new Quaternion(roundFloat(this.w, precision), roundFloat(this.x, precision), roundFloat(this.y, precision), roundFloat(this.z, precision)));
    },
};
Quaternion['ZERO'] = new Quaternion(0, 0, 0, 0);
Quaternion['ONE'] = new Quaternion(1, 0, 0, 0);
Quaternion['I'] = new Quaternion(0, 1, 0, 0);
Quaternion['J'] = new Quaternion(0, 0, 1, 0);
Quaternion['K'] = new Quaternion(0, 0, 0, 1);
Quaternion['EPSILON'] = 1e-16;
Quaternion['fromAxisAngle'] = function (axis, angle) {
    var halfAngle = angle * 0.5;
    var a = axis[0];
    var b = axis[1];
    var c = axis[2];
    var sin = Math.sin(halfAngle);
    var cos = Math.cos(halfAngle);
    var sinNorm = sin / Math.sqrt(a * a + b * b + c * c);
    return new Quaternion(cos, a * sinNorm, b * sinNorm, c * sinNorm);
};
Quaternion['fromBetweenVectors'] = function (u, v) {
    var a = u[0];
    var b = u[1];
    var c = u[2];
    var x = v[0];
    var y = v[1];
    var z = v[2];
    var dot = a * x + b * y + c * z;
    var w1 = b * z - c * y;
    var w2 = c * x - a * z;
    var w3 = a * y - b * x;
    return new Quaternion(dot + Math.sqrt(dot * dot + w1 * w1 + w2 * w2 + w3 * w3), w1, w2, w3).normalize();
};
Quaternion['fromEuler'] = function (phi, theta, psi, order) {
    var _x = theta * 0.5;
    var _y = psi * 0.5;
    var _z = phi * 0.5;
    var cX = Math.cos(_x);
    var cY = Math.cos(_y);
    var cZ = Math.cos(_z);
    var sX = Math.sin(_x);
    var sY = Math.sin(_y);
    var sZ = Math.sin(_z);
    if (order === undefined || order === 'ZXY') {
        return new Quaternion(cX * cY * cZ - sX * sY * sZ, sX * cY * cZ - cX * sY * sZ, cX * sY * cZ + sX * cY * sZ, cX * cY * sZ + sX * sY * cZ);
    }
    if (order === 'XYZ') {
        return new Quaternion(cX * cY * cZ - sX * sY * sZ, sX * cY * cZ + cX * sY * sZ, cX * sY * cZ - sX * cY * sZ, cX * cY * sZ + sX * sY * cZ);
    }
    if (order === 'YXZ') {
        return new Quaternion(cX * cY * cZ + sX * sY * sZ, sX * cY * cZ + cX * sY * sZ, cX * sY * cZ - sX * cY * sZ, cX * cY * sZ - sX * sY * cZ);
    }
    if (order === 'ZYX') {
        return new Quaternion(cX * cY * cZ + sX * sY * sZ, sX * cY * cZ - cX * sY * sZ, cX * sY * cZ + sX * cY * sZ, cX * cY * sZ - sX * sY * cZ);
    }
    if (order === 'YZX') {
        return new Quaternion(cX * cY * cZ - sX * sY * sZ, sX * cY * cZ + cX * sY * sZ, cX * sY * cZ + sX * cY * sZ, cX * cY * sZ - sX * sY * cZ);
    }
    if (order === 'XZY') {
        return new Quaternion(cX * cY * cZ + sX * sY * sZ, sX * cY * cZ - cX * sY * sZ, cX * sY * cZ - sX * cY * sZ, cX * cY * sZ + sX * sY * cZ);
    }
    return null;
};
Quaternion['average'] = function (quaternions, weights) {
    if (weights === void 0) { weights = undefined; }
    if (weights === undefined) {
        weights = math.ones(quaternions.length);
    }
    else {
        if (weights.length !== quaternions.length) {
            throw new Error('Average requires one weight per quaternion');
        }
        weights = math.matrix(weights);
    }
    var weightedQuaternions = weights.map(function (w, i) { return math.multiply(w, quaternions[i]); });
    var Q = math.transpose(weightedQuaternions);
    var Q_QT = math.multiply(Q, math.transpose(Q));
    var eigenvectors = math.eigs(Q_QT).vectors;
    var lastColumn = eigenvectors.size()[1] - 1;
    var qAvg = math.flatten(math.column(eigenvectors, lastColumn).valueOf());
    return new Quaternion(qAvg);
};
//# sourceMappingURL=quaternion.js.map